<template>
    <kiosk-org-unit-details :teamId="props.teamId" :orgUnitId="props.orgUnitId">
        <div class="col-span-12 lg:col-span-10 flex flex-col w-full justify-center">
            <div class="max-w-(--breakpoint-sm) flex flex-col">
                <page-callout section="kiosk-org-unit-billing-config" class="mb-4" />

                <page-header>Billing Config</page-header>

                <vx-expandable-card>
                    <template v-slot:header>Enable Billing</template>
                    <template v-slot:content>
                        <div class="flex flex-col gap-4 mt-3">
                            <p class="text-sm text-gray-700">
                                This allows for the confirmation of additional base fees, the routing of messaging usage
                                charges, and the application of a minimum spend commit.
                            </p>

                            <div class="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                <div class="flex gap-3">
                                    <div class="flex-shrink-0">
                                        <font-awesome-icon
                                            :icon="faExclamationTriangle"
                                            class="text-amber-600 text-lg"
                                        />
                                    </div>
                                    <div class="flex flex-col gap-2">
                                        <p class="font-semibold text-sm text-amber-900">
                                            Important: This action is permanent!
                                        </p>
                                        <ul class="list-disc list-inside text-sm text-amber-800 space-y-1">
                                            <li>Once enabled, billing cannot be disabled</li>
                                            <li>The organizational unit cannot be deleted after enabling billing</li>
                                            <li>
                                                All charges will initially route to corporate until pricing is
                                                configured
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div class="flex gap-3">
                                    <div class="flex-shrink-0">
                                        <font-awesome-icon :icon="faLightbulb" class="text-blue-600 text-lg" />
                                    </div>
                                    <div class="flex flex-col gap-2">
                                        <p class="font-semibold text-sm text-blue-900">What happens after enabling?</p>
                                        <ul class="list-disc list-inside text-sm text-blue-800 space-y-1">
                                            <li>A Stripe customer account will be created</li>
                                            <li>Payment terms default to automatic (immediate) payment</li>
                                            <li>You can configure monthly pricing to route charges to this OU</li>
                                            <li>Invoices will be generated separately from corporate</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <vx-button
                                v-if="team?.status !== 'closed'"
                                data-test="enable-billing-button"
                                @click="enableBilling"
                                color="primary"
                                size="lg"
                                :loading="enabling"
                                class="w-full"
                            >
                                Enable Billing
                            </vx-button>

                            <p v-if="team?.status === 'closed'" class="text-sm text-gray-500 text-center">
                                Billing cannot be enabled for organizational units on closed teams.
                            </p>
                        </div>
                    </template>
                </vx-expandable-card>
            </div>
        </div>
    </kiosk-org-unit-details>
</template>

<script setup>
import { ref } from 'vue';
import KioskOrgUnitDetails from '~/components/kiosk/kiosk-org-units/details/KioskOrgUnitDetails.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageCallout from '~/components/general/PageCallout.vue';
import { VxButton, VxExpandableCard } from '@voxie/frontend-components';
import { useOrgUnitDetails } from '~/components/kiosk/kiosk-org-units/composables/useOrgUnitDetails';
import adminOrgUnitsClient from '~/services/adminOrgUnitsClient';
import { useToasted } from '~/composables/useToasted';
import { catchRequestError } from '~/utils/request';
import { faExclamationTriangle, faLightbulb } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    orgUnitId: {
        type: Number,
        required: true,
    },
});

const { team, refreshOrgUnit } = useOrgUnitDetails();
const toasted = useToasted();
const enabling = ref(false);

const enableBilling = async () => {
    if (enabling.value) {
        return;
    }

    enabling.value = true;

    try {
        await adminOrgUnitsClient.enableBilling(props.teamId, props.orgUnitId);

        // Refresh org unit to get updated is_billable status
        await refreshOrgUnit(props.teamId, props.orgUnitId);

        toasted.success('Billing has been enabled for this organizational unit');

        // Component will automatically re-render to show config page due to is_billable change
    } catch (e) {
        toasted.error(catchRequestError(e));
    } finally {
        enabling.value = false;
    }
};
</script>
